import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import axios from "axios";
import getCardsByLanguage from './cards';
import TarotCard from './components/TarotCard';
import ReadingDisplay from './components/ReadingDisplay';
import MonthlyTimeline from './components/MonthlyTimeline';
import ReactMarkdown from "react-markdown";
import SixYaoCoinAnimation from './components/SixYaoCoinAnimation';
import SixYaoDisplay from './components/SixYaoDisplay';
// 🌟 MODIFIED: 引入新的反推函数 generateHexagramsAndDeriveYao
import { determineSixYaoResults, generateHexagramsAndDeriveYao } from './utils/sixyaoData';
import remarkGfm from "remark-gfm";
import { usePersistedState } from './hooks/usePersistedState';
import { useLanguage } from './context/LanguageContext';
import { Lunar } from 'lunar-javascript';
import html2canvas from "html2canvas";
import './App.css';

// 路由常量
const ROUTES = {
    TAROT: '/tarot',
    SIXYAO: '/sixyao'
};

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "http://localhost:3001").replace(/\/$/, "");
const AI_PATH = `${API_BASE}/api/askAI`;

// 定义预测类型常量
const PREDICTION_TYPES = {
    TAROT: 'tarot',
    SIXYAO: 'sixyao',
};

// 定义牌阵类型常量
const SPREAD_TYPES = {
    TIME_FLOW: 'time_flow',		// 三张牌：时间流
    CELTIC_CROSS: 'celtic_cross'	// 十张牌：凯尔特十字
};




// 获取当前的年月日时干支 (用于六爻断卦)
const getCurrentGanZhi = () => {
    const now = new Date();
    const lunar = Lunar.fromDate(now);
    console.log("DEBUG: Lunar Object Day:", lunar.getDayInGanZhi());
    const result = {
        date: lunar.getSolar().toString(),
        year: lunar.getYearInGanZhi() + '年',
        month: lunar.getMonthInGanZhi() + '月',
        day: lunar.getDayInGanZhi() + '日',
        time: lunar.getTimeInGanZhi() + '时',
        ganZhiString: `${lunar.getYearInGanZhi()}${lunar.getMonthInGanZhi()}${lunar.getDayInGanZhi()}${lunar.getTimeInGanZhi()}`
    };
    console.log("DEBUG: getCurrentGanZhi returning:", result);
    return result
};

// 根据六爻数 (6-9) 生成卦爻符号
const getSymbolFromYao = (yao) => {
    switch (yao) {
        case 6: return 'X'; // 老阴 (阴爻变阳)
        case 7: return '—'; // 少阳 (阳爻不变)
        case 8: return '— —'; // 少阴 (阴爻不变)
        case 9: return 'O'; // 老阳 (阳爻变阴)
        default: return '—';
    }
};

// Generate labels for the next 12 calendar months, e.g. ["3月","4月",..."2月"]
const getNextMonths = () => {
    const now = new Date();
    return Array.from({ length: 12 }, (_, i) => {
        const d = new Date(now.getFullYear(), now.getMonth() + i + 1, 1);
        return `${d.getMonth() + 1}月`;
    });
};

const AppContent = () => {
    const { t, language, setLanguage } = useLanguage();
    const navigate = useNavigate(); // 用于路由跳转
    const location = useLocation(); // 用于获取当前路由信息
    const isTarot = location.pathname === '/' || location.pathname === ROUTES.TAROT;
    const isSixYao = location.pathname === ROUTES.SIXYAO;
    // 使用 isTarot/isSixYao 决定当前模式
    const route = isSixYao ? 'sixyao' : 'tarot';
    
    
    // 控制页面方向（RTL / LTR）
    useEffect(() => {
        document.dir = language === 'ar' ? 'rtl' : 'ltr';
    }, [language]); // 每次语言改变都会更新

    // wake up server
    useEffect(() => {
        const wakeUpBackend = async () => {
            try {
                await fetch('https://beer-8jon.onrender.com/health', {
                    method: 'GET',
                    signal: AbortSignal.timeout(5000)
                });
                console.log('server awake');
            } catch (error) {
                console.log('server wakeup called');
            }
        };
        wakeUpBackend();
    }, []);

    //stripe checkout
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');
        if (sessionId) {
            // 直接解锁内容
            setIsUnlocked(true);
        }
    }, []);

    // ----------------------------------------------------
    // 新增：路由和六爻状态
    // ----------------------------------------------------


    // Stages: question -> drawing -> reading
    const [stage, setStage] = usePersistedState(`${route}-stage`, 'question');
    const [question, setQuestion] = usePersistedState(`${route}-question`, '');
    
    // 个人信息，两个模块共用
    const [birthYear, setBirthYear] = usePersistedState('birthYear', '');
    const [birthMonth, setBirthMonth] = usePersistedState('birthMonth', '');
    const [birthDate, setBirthDate] = usePersistedState('birthDate', '');
    const [country, setCountry] = usePersistedState('country', '');
    const [city, setCity] = usePersistedState('city', '');
    const [gender, setGender] = usePersistedState('gender', '');
    const [isPersonalInfoExpanded, setIsPersonalInfoExpanded] = usePersistedState('personalInfoExpanded', false);
    // Tarot 状态
    const [drawnCards, setDrawnCards] = usePersistedState(`tarot-drawnCards`, []);
    const [displayCards, setDisplayCards] = usePersistedState(`tarot-displayCards`, []);
    const [reading, setReading] = usePersistedState(`tarot-reading`, '');
    const [currentSpread, setCurrentSpread] = usePersistedState(`tarot-spread`, SPREAD_TYPES.TIME_FLOW);

    // 12-month timeline state (one per route)
    const [tarotTimeline, setTarotTimeline] = usePersistedState('tarot-timeline', []);
    const [sixYaoTimeline, setSixYaoTimeline] = usePersistedState('sixyao-timeline', []);

    // SixYao 状态
    const [yaoResults, setYaoResults] = usePersistedState('sixyao-results', []); // [7, 8, 9, 6, 7, 8]
    const [displayYao, setDisplayYao] = usePersistedState('sixyao-display', []); // 用于动画，逐个显示
    const [ganZhi, setGanZhi] = usePersistedState('sixyao-ganzhi', {}); // 起卦时辰干支
    const [sixYaoReading, setSixYaoReading] = usePersistedState('sixyao-reading', ''); // 六爻解读结果
    const [hexagramNames, setHexagramNames] = usePersistedState('sixyao-hexagrams', { main: '', change: '' });

    // 通用状态
    const [loading, setLoading] = useState(false);
    const [isAnimationDone, setIsAnimationDone] = useState(false);
    const [isUnlocked, setIsUnlocked] = useState(false);
    const resultRef = useRef(null);
    // Keep last AI request data so retry can re-send it
    const lastAIDataRef = useRef({});




    // 根据当前的route确定最终的reading和setReading函数
    const finalReading = isTarot ? reading : sixYaoReading;
    const setFinalReading = isTarot ? setReading : setSixYaoReading;
    const finalTimeline = isTarot ? tarotTimeline : sixYaoTimeline;
    const setFinalTimeline = isTarot ? setTarotTimeline : setSixYaoTimeline;
    
    const handleExport = async () => {
        if (!resultRef.current) return;
        
        const backgroundStyle = isSixYao ? '#0d0d0d' : '#0b0d14'; // 六爻使用更深的黑色
        try {
            const canvas = await html2canvas(resultRef.current, {
                scale: 2,
                backgroundColor: backgroundStyle, // 或者直接使用CSS背景色
                useCORS: true, // 如果有跨域图片
            });
    
            const link = document.createElement('a');
            link.href = canvas.toDataURL('image/png');
            link.download = `${isTarot ? 'tarot' : 'sixyao'}-reading.png`;
            link.click();
        } catch (err) {
            console.error('Export failed:', err);
        }
    };
    
    // 凯尔特十字的位置名称 (建议加到你的翻译文件中，这里仅作示例)
    // 对应位置：1.现状 2.阻碍 3.潜意识 4.过去 5.意识/目标 6.未来 7.自我 8.环境 9.希望/恐惧 10.结果
    const getPositionNames = (spread) => {
        if (spread === SPREAD_TYPES.TIME_FLOW) {
            return t('positions'); // 假设 t('positions') 返回 ['过去', '现在', '未来']
        } else {
            return t('positionsCeltic'); 
        }
    };

    

    // 随机抽三张牌
    const drawCards = (count = 3) => {
        const currentDeck = getCardsByLanguage(language);
        const deckCopy = [...currentDeck];
        const selected = [];
    
        for (let i = 0; i < count; i++) {
            const idx = Math.floor(Math.random() * deckCopy.length);
            const card = deckCopy.splice(idx, 1)[0];
    
            const isUpright = Math.random() < 0.5;
            selected.push({
                ...card,
                orientation: isUpright ? t('upright') : t('reversed'),
                meaning: isUpright ? card.upright : card.reversed
            });
        }
        return selected;
    };

    // 抽牌动画函数
    const startCardDrawing = (selectedCards) => {
        setDrawnCards(selectedCards);
        setDisplayCards(new Array(selectedCards.length).fill(null));
        setStage("drawing");

        // 动态调整速度：如果是凯尔特十字，每张牌间隔缩短到 800ms 或 1000ms
        const intervalBase = 3000;
        const initialDelay = 1500;
        // 逐张显示牌
        selectedCards.forEach((card, index) => {
            const delay = index === 0 ? initialDelay : initialDelay + index * intervalBase;
            setTimeout(() => {
                setDisplayCards(prev => {
                    const newDisplay = [...prev];
                    newDisplay[index] = card;
                    return newDisplay;
                });

                if (index === selectedCards.length - 1) {
                    setTimeout(() => {
                        // 🌟 MODIFIED: 动画完成，设置标志位，等待AI请求完成
                        setIsAnimationDone(true);
                        setStage("reading");
                    }, 1000);
                }
            }, delay);
        });
    };

    
    // ----------------------------------------------------
    // SixYao 逻辑
    // ----------------------------------------------------

    // 🌟 MODIFIED: 使用 generateHexagramsAndDeriveYao 实现 卦 -> 爻 的逻辑
    const generateSixYao = () => {
        const result = generateHexagramsAndDeriveYao();
        return result; 
    };
    
    const handleAnimationComplete = useCallback(() => {
        // 动画组件通知我们它完成了所有动画
        setStage("reading");
    }, []);
    
    const startYaoDrawing = useCallback((results) => {
        if (!Array.isArray(results)) return;
        setYaoResults(results);
        setGanZhi(getCurrentGanZhi()); 
        setDisplayYao([]);
        setStage("drawing");
        
        const intervalBase = 2000;
        const initialDelay = 1000;
        
        results.forEach((yao, index) => {
            const delay = initialDelay + index * intervalBase;
            setTimeout(() => {
                setDisplayYao(prev => {
                    // 仅更新 displayYao 数组
                    return [...prev, yao];
                });
    
                // 🌟 移除此处所有的 stage 切换逻辑
            }, delay);
        });
    }, []);

    const handleRetry = async () => {
        setFinalReading('');
        setFinalTimeline([]);
        setLoading(true);
        await startAIRequest(lastAIDataRef.current);
    };

    const handleReset = () => {
        // 重置时只重置当前路由下的数据，并且重置stage为question
        const currentRoute = isSixYao ? 'sixyao' : 'tarot';
        
        setStage("question");
        setQuestion("");
        setFinalReading(""); 
        
        if (isTarot) {
            setDrawnCards([]);
            setDisplayCards([]);
            setTarotTimeline([]);
        } else if (isSixYao) {
            setYaoResults([]);
            setDisplayYao([]);
            setGanZhi({});
            setSixYaoTimeline([]);
        }
        
        setLoading(false);
        // 🌟 NEW: 重置动画完成标志
        setIsAnimationDone(false); 
        const url = new URL(window.location.href);
        url.searchParams.delete('session_id');
        window.history.pushState({}, '', url.toString());
    };

    // AI请求函数
    const startAIRequest = async (data = {}) => {
        const birthInfo = birthYear && birthMonth && birthDate 
            ? `${birthYear}-${birthMonth.padStart(2, '0')}-${birthDate.padStart(2, '0')}`
            : 'none';
        
        const locationInfo = country || city ? `${country}${country && city ? ', ' : ''}${city}` : 'none';
        const genderInfo = gender || 'none';

        const context = {
            question: question,
            bio: { gender: genderInfo, birth: birthInfo, location: locationInfo },
            language: language === 'zh' ? 'Simplified Chinese (简体中文, use 简体汉字)'
                    : language === 'zhHant' ? 'Traditional Chinese (繁體中文, use 繁體漢字)'
                    : language === 'ko' ? 'Korean'
                    : language === 'ja' ? 'Japanese'
                    : language === 'en' ? 'English'
                    : 'Arabic',
            type: route, // tarot or sixyao
            data: {},
        };
        let specificInstructions = '';

        if (isTarot) {
            // 塔罗牌数据构建
            let cardsString = '';
            (drawnCards || []).forEach((card, index) => { 
                const posName = getPositionNames(currentSpread)[index];
                cardsString += `${index + 1}. [${posName}] - ${card.name} (${card.orientation}): ${card.meaning}\n`;
            });
            context.data = {
                cards: cardsString,
                spread: currentSpread,
            };
        } else if (isSixYao) {
            // 使用传递进来的数据，而不是从异步状态中读取
            const { yaoValues: requestYao, mainHexagram: requestMain, changeHexagram: requestChange, ganZhi: requestGanZhi } = data;
            // 定义爻位名称，此处假设中文翻译（与 getSymbolFromYao 的逻辑配合）
            const yaoPositions = ['初爻', '二爻', '三爻', '四爻', '五爻', '上爻'];
            
            // 🌟 MODIFIED: 仅构建动爻摘要，去除了静爻信息
            const movingLinesSummary = (requestYao || []).map((yao, index) => {
                if (yao === 6 || yao === 9) {
                    const yaoPos = yaoPositions[index]; 
                    const name = yao === 9 ? '老阳 (变阴)' : '老阴 (变阳)';
                    const symbol = yao === 9 ? 'O' : 'X';
                    // Format: 初爻: 老阳 (变阴), 符号: O, 爻数: 9
                    return `${yaoPos}: ${name}`;
                }
                return null;
            }).filter(line => line !== null).join('\n') || t('noMovingYao') || '无动爻(情况稳定，短期内难以改变)'; // Added '无动爻' fallback
            
            context.data = {
                ganZhi: requestGanZhi, // 🌟 RETAINED: 干支信息对于六爻分析至关重要
                movingLines: movingLinesSummary, // 🌟 NEW: 仅传递动爻信息
                mainHexagram: requestMain,
                changeHexagram: requestChange,
            };
        }
        
        // 核心 Prompt 模板
        let prompt;
        if (isTarot) {
            if (currentSpread === SPREAD_TYPES.CELTIC_CROSS) {
      // 凯尔特十字：要求整体性，不要逐张罗列，限制字数
      specificInstructions = `
**Layout:** Celtic Cross (10 Cards).
**Analysis Strategy:**
1. **The Immediate Reality:** Synthesize Cards 1, 2, and 6. State exactly what is happening *now* and in the next 3 weeks.
2. **Internal & External Drivers**: Use Cards 7 (Self) and 8 (Environment) to explain the "Why." Identify if the person's own attitude or an external person/force is the primary catalyst for the predicted outcome.
3. **The Turning Point:** Identify the single card that shifts the energy from the Past (Card 4 or 5) to the Outcome (Card 10).
4. **The 3-Month Verdict:** Use Card 6 (Near Future) and Card 10 (Final Outcome) to provide a firm 90-day forecast. If the cards conflict, the higher-numbered Major Arcana takes precedence.
5. **Long-Term Projection**: Extend the logic of Card 10 across the 12-month timeline. If Card 9 (Hopes/Fears) is a warning, use it to define the "dip" or "risk" in the later months of the year.
**Output:** Holistic, predictive, and assertive.
`;
    } else {
      // 时间流：保持原有详细风格，并加入具体事件和人物描述
      specificInstructions = `
**Layout:** Three Card Spread (Time Flow).
**Analysis Strategy:** Interpret Past, Present, and Future sequentially, then provide a synthesis.
`;
    }
        {
        const nextMonths = getNextMonths();
        const timelineRows = nextMonths.map((label, i) =>
            `    {"month": ${i + 1}, "label": "${label}", "development": "...", "hint": "..."}`
        ).join(',\n');
        prompt = `
You are a tarot reader. Answer the person's actual question through the cards. Every sentence must serve that question directly.

THE QUESTION: "${context.question}"

WRITING STYLE:
1. **The Lead:** Start with one sharp, assertive sentence declaring the outcome; include a specific timeframe if applicable.
2. Use "will" and "shall"—avoid "might," "could," or "it depends."
3. **No Hedging:** Do not provide "alternative" positive interpretations if the Hexagrams are negative.

CARDS:
${context.data.cards}

Bio (use lightly): ${context.bio.gender}, born ${context.bio.birth}, ${context.bio.location}

${specificInstructions}

LANGUAGE: ${context.language}

---
OUTPUT: Return ONLY a valid JSON object with this exact structure (no markdown wrapper):
{
  "reading": "full reading text here — use \\n\\n between paragraphs, ## for section headings",
  "timeline": [
${timelineRows}
  ]
}
TIMELINE RULES:
- "development": Always use "[min, max]" (e.g., "[+2, +3], [-2,+10]").
- Uncertainty: As time passes, especially after month 3, widen the range between min and max significantly to show increasing uncertainty.
- Scale: Use -20 to +20 with +/- signs.
- "hint": 4-6 concrete words in ${context.language}.
`; }
        } 
        else if (isSixYao) {
            // 六爻 Prompt 逻辑
            {
            const nextMonths = getNextMonths();
            const timelineRows = nextMonths.map((label, i) =>
                `    {"month": ${i + 1}, "label": "${label}", "development": "...", "hint": "..."}`
            ).join(',\n');
            prompt = `
You are an I Ching interpreter. Answer the person's actual question through the hexagram. Every sentence must serve that question directly.

THE QUESTION: "${context.question}"

WRITING STYLE:
1. **The Lead:** Start with one sharp, assertive sentence declaring the outcome; include a specific timeframe if applicable.
2. Use "will" and "shall"—avoid "might," "could," or "it depends."
3. **No Hedging:** Do not provide "alternative" positive interpretations if the Hexagrams are negative.
4. No jargon unless it is necessary and explained in an easy to understand way.

HEXAGRAM DATA:
- Main Hexagram (本卦): ${context.data.mainHexagram}
- Changing Hexagram (变卦): ${context.data.changeHexagram}
- Moving Lines: ${context.data.movingLines || '无动爻 — 局面稳定，观其自然'}
- Divination time (干支): ${context.data.ganZhi.ganZhiString}

Bio (use lightly): ${context.bio.gender}, born ${context.bio.birth}, ${context.bio.location}

LANGUAGE: ${context.language}

---
OUTPUT: Return ONLY a valid JSON object with this exact structure (no markdown wrapper):
{
  "reading": "full reading text here — use \\n\\n between paragraphs, ## for section headings",
  "timeline": [
${timelineRows}
  ]
}
TIMELINE RULES:
- "development": Always use "[min, max]" (e.g., "[+2, +3], [-2,+10]").
- Uncertainty: As time passes, especially after month 3, widen the range between min and max significantly to show increasing uncertainty.
- Scale: Use -20 to +20 with +/- signs.
- "hint": 4-6 concrete words in ${context.language}.
`; }
        }

        try {
            const res = await axios.post(AI_PATH, { prompt, jsonMode: true });
            let readingText = res.data.text || '';
            let timelineData = Array.isArray(res.data.timeline) ? res.data.timeline : [];

            // Safety net: if server JSON parse failed and sent raw JSON as text, parse it here
            const trimmed = readingText.trim();
            if (trimmed.startsWith('{') || trimmed.startsWith('```')) {
              try {
                const cleaned = trimmed.replace(/^```(?:json)?\s*/i, '').replace(/\s*```\s*$/i, '').trim();
                const parsed = JSON.parse(cleaned);
                if (parsed.reading) {
                  readingText = parsed.reading;
                  if (Array.isArray(parsed.timeline) && parsed.timeline.length > 0) {
                    timelineData = parsed.timeline;
                  }
                }
              } catch (_) { /* not JSON – keep as-is */ }
            }

            setFinalReading(readingText || t('noResult'));
            setFinalTimeline(timelineData);
        } catch (err) {
            console.error("Error fetching reading:", err);
            setFinalReading('__FAILED__');
            setFinalTimeline([]);
        } finally {
            setLoading(false);
        }
    };

    // When user submits question
    const handleSubmit = async () => {
        if (!question.trim()) {
            // 🌟 MODIFIED: 统一错误处理，避免 alert
            console.error(t('noQuestion'));
            return;
        }
        let sixYaoData = {};
    
        // 1. 根据当前路由决定抽牌还是摇卦
        if (isTarot) {
            // 将随机选择的概率调高，确保凯尔特十字有机会出现
            const randomSpread = Math.random() < 0 ? SPREAD_TYPES.TIME_FLOW : SPREAD_TYPES.CELTIC_CROSS; 
            setCurrentSpread(randomSpread);
            const cardCount = randomSpread === SPREAD_TYPES.TIME_FLOW ? 3 : 10;
            const selectedCards = drawCards(cardCount);
            startCardDrawing(selectedCards);
        } else if (isSixYao) {
            let attempts = 0;
            let safeGanZhiData = {};
            
            // 循环尝试，直到获取到一个有键的对象（最多尝试5次）
            while (Object.keys(safeGanZhiData).length === 0 && attempts < 5) {
                safeGanZhiData = getCurrentGanZhi();
                attempts++;
                // 如果第一次失败，稍微暂停一下让库完成初始化（可选，但更安全）
                if (Object.keys(safeGanZhiData).length === 0) {
                     await new Promise(resolve => setTimeout(resolve, 10)); // 暂停 10ms
                }
            }
            
            // 🌟 2. 如果多次尝试后仍失败，回退到状态 ganZhi (如果它最终被正确设置)
            if (Object.keys(safeGanZhiData).length === 0) {
                 safeGanZhiData = ganZhi; // 这里的 ganZhi 是组件顶层的 state
            }
            
            console.log("DEBUG: final safeGanZhiData structure:", safeGanZhiData);
            const { yaoValues, mainHexagram, changeHexagram } = generateSixYao();
            setHexagramNames({ main: mainHexagram, change: changeHexagram });
            setYaoResults(yaoValues); // 确保 setYaoResults 也在此处同步设置
            startYaoDrawing(yaoValues); // 用于动画
            sixYaoData = { yaoValues, mainHexagram, changeHexagram, ganZhi: safeGanZhiData, };
            
        }
    
        setLoading(true);
        setIsAnimationDone(false);
        const aiData = isSixYao ? sixYaoData : {};
        lastAIDataRef.current = aiData;
        startAIRequest(aiData);
    };

    
    const hasPersonalInfo = birthYear || birthMonth || birthDate || country || city || gender;

    // 根据当前路由确定背景图
    const backgroundImageUrl = isTarot 
            ? `url('https://feelstuck.com/background.jpeg')` 
            : `url('https://example.com/sixyao_background.jpg')`;

    return (
        <div
            className={`app-root ${isSixYao ? 'sixyao-theme' : 'tarot-theme'}`}
            style={{ backgroundImage: backgroundImageUrl }}
        >
            <div className="overlay" />

            <div className="container">
                <div className="language-switcher">
                    <button 
                        onClick={() => setLanguage('en')}
                        className={language === 'en' ? 'active' : ''}
                    >
                        English
                    </button>
                    <button 
                        onClick={() => setLanguage('zh')}
                        className={language === 'zh' ? 'active' : ''}
                    >
                        中文(简)
                    </button>
                    <button 
                        onClick={() => setLanguage('zhHant')}
                        className={language === 'zhHant' ? 'active' : ''}
                    >
                        中文(繁)
                    </button>
                    <button 
                        onClick={() => setLanguage('ko')}
                        className={language === 'ko' ? 'active' : ''}
                    >
                        한국어
                    </button>
                    <button 
                        onClick={() => setLanguage('ja')}
                        className={language === 'ja' ? 'active' : ''}
                    >
                        日本語
                    </button>
                    <button 
                        onClick={() => setLanguage('ar')}
                        className={language === 'ar' ? 'active' : ''}
                    >
                        العربية
                    </button>
                </div>

                {/* 头部导航/路由切换 */}
                <div className="tab-navigator">
                    <button 
                        className={`tab-button ${isTarot ? 'active' : ''}`}
                        onClick={() => { navigate(ROUTES.TAROT); handleReset();}} // 切换时清空数据，重新开始
                    >
                        {t('tabTarot')}
                    </button>
                    <button 
                        className={`tab-button ${isSixYao ? 'active' : ''}`}
                        onClick={() => { navigate(ROUTES.SIXYAO); handleReset();}} // 切换时清空数据，重新开始
                    >
                        {t('tabSixYao')}
                    </button>
                </div>
                            
                <header className="header">
                    <div>
                        {(stage === 'drawing' || stage === 'reading') && question ? (
                            <h1 className="title question-as-title">{question}</h1>
                        ) : (
                            <h1 className="title">{isTarot ? t('titleTarot') : t('titleSixYao')}</h1>
                        )}
                    </div>
                </header>
            
                {stage === "question" && (
                    <section className="card-panel">
                        <div className="question-form">
                            <textarea
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                placeholder={t('placeholder')}
                                className="input-textarea"
                                rows={4}
                            />

                            {/* 展开/折叠按钮 */}
                            <div className="expand-section">
                                <button
                                    type="button"
                                    className="expand-button"
                                    onClick={() => setIsPersonalInfoExpanded(!isPersonalInfoExpanded)}
                                >
                                    <label className="label">
                                        {isPersonalInfoExpanded ? t('collapseInfo') : t('expandInfo')}
                                        {hasPersonalInfo && !isPersonalInfoExpanded && ' ✓'}
                                    </label>
                                    <span className={`expand-arrow ${isPersonalInfoExpanded ? 'expanded' : ''}`}>
                                        {isPersonalInfoExpanded ? '▲' : '▼'}
                                    </span>
                                </button>
                            </div>

                            {/* 条件渲染个人信息表格 */}
                            {isPersonalInfoExpanded && (
                                <div className="personal-info-grid">
                                    <div className="info-group">
                                        <label className="label">{t('birthYear')}</label>
                                        <input
                                            type="number"
                                            value={birthYear}
                                            onChange={(e) => setBirthYear(e.target.value)}
                                            placeholder="1990"
                                            className="input-field"
                                            min="1900"
                                            max="2100"
                                        />
                                    </div>
    
                                    <div className="info-group">
                                        <label className="label">{t('birthMonth')}</label>
                                        <input
                                            type="number"
                                            value={birthMonth}
                                            onChange={(e) => setBirthMonth(e.target.value)}
                                            placeholder="1"
                                            className="input-field"
                                            min="1"
                                            max="12"
                                        />
                                    </div>
    
                                    <div className="info-group">
                                        <label className="label">{t('birthDay')}</label>
                                        <input
                                            type="number"
                                            value={birthDate}
                                            onChange={(e) => setBirthDate(e.target.value)}
                                            placeholder="15"
                                            className="input-field"
                                            min="1"
                                            max="31"
                                        />
                                    </div>
    
                                    <div className="info-group">
                                        <label className="label">{t('country')}</label>
                                        <input
                                            type="text"
                                            value={country}
                                            onChange={(e) => setCountry(e.target.value)}
                                            placeholder={t('countryPlaceholder')}
                                            className="input-field"
                                        />
                                    </div>
    
                                    <div className="info-group">
                                        <label className="label">{t('city')}</label>
                                        <input
                                            type="text"
                                            value={city}
                                            onChange={(e) => setCity(e.target.value)}
                                            placeholder={t('cityPlaceholder')}
                                            className="input-field"
                                        />
                                    </div>
    
                                    <div className="info-group">
                                        <label className="label">{t('gender')}</label>
                                        <select
                                            value={gender}
                                            onChange={(e) => setGender(e.target.value)}
                                            className="input-field"
                                        >
                                            <option value="">{t('select')}</option>
                                            <option value="男">{t('male')}</option>
                                            <option value="女">{t('female')}</option>
                                            <option value="其他">{t('others')}</option>
                                        </select>
                                    </div>
                                </div>
                            )}

                            {/* ── Pre-submit hint ── */}
                            <p className="casting-hint">
                                {isTarot ? t('castingHintTarot') : t('castingHintSixYao')}
                            </p>

                            <button
                                onClick={handleSubmit}
                                className="primary-button"
                                disabled={!question.trim()}
                                aria-disabled={!question.trim()}
                            >
                                {isTarot ? t('buttonTextTarot') : t('buttonTextSixYao')}
                            </button>
                        </div>
                    </section>
                )}



                {/* 抽牌/摇卦和解读阶段 */}
                        {(stage === "drawing" || stage === "reading") && (
                            <>
                                <section className="reading-area" ref={resultRef}>
                                    {/* ------------------- 塔罗牌显示 ------------------- */}
                                    {isTarot && (
                                        <div className={`cards-container ${currentSpread === SPREAD_TYPES.CELTIC_CROSS ? 'layout-celtic' : 'layout-linear'}`}>
                                            {displayCards.map((card, i) => (
                                                <div key={i} className={`card-wrapper position-${i}`}>
                                                    <TarotCard
                                                        card={card}
                                                        position={getPositionNames(currentSpread)[i]}
                                                        index={i}
                                                        isPlaceholder={!card}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* ------------------- 六爻显示 ------------------- */}
                                    {isSixYao && (
                                        <div className="sixyao-container">
                                            {/* ------------------- SixYaoCoinAnimation ------------------- */}
                                            {stage === "drawing" && (
                                                <SixYaoCoinAnimation 
                                                    yaoResults={yaoResults}
                                                    displayYao={displayYao} 
                                                    currentHexagramName={hexagramNames.main}
                                                    // 🌟 新增：完成回调
                                                    onAnimationComplete={handleAnimationComplete} 
                                                />
                                            )}

                                            {/* ------------------- SixYaoDisplay ------------------- */}
                                            {(stage === "reading" || stage === "displaying_yao") && (
                                                <SixYaoDisplay 
                                                    yaoResults={yaoResults} 
                                                    ganZhi={ganZhi}
                                                    hexagramNames={hexagramNames}
                                                />
                                            )}
                                        </div>
                                    )}
                                    
                                    {/* ------------------- 十二个月时间线 ------------------- */}
                                    {stage === "reading" && finalTimeline.length > 0 && (
                                        <MonthlyTimeline
                                            timeline={finalTimeline}
                                            type={isTarot ? 'tarot' : 'sixyao'}
                                        />
                                    )}

                                    {/* ------------------- 解读显示 ------------------- */}
                                    {stage === "reading" && (
                                        <ReadingDisplay
                                            reading={finalReading}
                                            loading={loading}
                                            isUnlocked={isUnlocked}
                                            onUnlock={() => setIsUnlocked(true)}
                                            onRetry={handleRetry}
                                        />
                                    )}

                                    {/* ------------------- 声明模块 ------------------- */}
                                    {stage === "reading" && finalReading && finalReading !== '__FAILED__' && !loading && (
                                        <div className="reading-disclaimer">
                                            <span className="disclaimer-title">{t('disclaimerTitle')}</span>
                                            <span className="disclaimer-sep"> · </span>
                                            <span className="disclaimer-body">{t('disclaimerText')}</span>
                                        </div>
                                    )}
                                </section>

                                {/* 按钮区域（不截图） */}
                                {stage === "reading" && (
                                    <>
                                        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                                            <button
                                                className="outline-button export-button"
                                                onClick={handleExport}
                                            >
                                                {t('exportScreenshot')}
                                            </button>
                                        </div>
                                        <div className="actions" style={{textAlign: 'center', marginTop: '1rem' }}>
                                            <button className="outline-button" onClick={handleReset}>
                                                {t('resetText')}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </>
                        )}


                        <footer className="footer">
                            <div>✦ Crafted by Jojo ✦</div>
                        </footer>
                    </div>
                </div>
            );
};

// ----------------------------------------------------
// 主 App 组件 (包含 Router 逻辑)
// ----------------------------------------------------
const App = () => (
    <BrowserRouter>
        <Routes>
            {/* 将 /tarot 和 / 路由都映射到 AppContent */}
            <Route path="/" element={<AppContent />} />
            <Route path={ROUTES.TAROT} element={<AppContent />} />
            {/* 新增的六爻路由 */}
            <Route path={ROUTES.SIXYAO} element={<AppContent />} />
        </Routes>
    </BrowserRouter>
);

export default App;
